import { useEffect, useId, type ReactNode } from "react";
import { X } from "lucide-react";
import { useI18n } from "../../i18n/I18nProvider";

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** When false, Escape and the backdrop do not close the dialog (used for one-time secrets). */
  dismissible?: boolean;
}

/** Protected-focus dialog: role=dialog, labelled by its title, closes on Escape unless locked. */
export function Modal({ title, onClose, children, dismissible = true }: ModalProps) {
  const { t } = useI18n();
  const titleId = useId();

  useEffect(() => {
    if (!dismissible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dismissible, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full overflow-y-auto rounded-t-panel bg-board p-5 shadow-board sm:max-w-md sm:rounded-panel"
      >
        <div className="mb-4 flex items-start justify-between gap-3 border-b-2 border-ink pb-3">
          <h2
            id={titleId}
            className="font-display text-2xl font-extrabold uppercase tracking-[0.01em]"
          >
            {title}
          </h2>
          {dismissible ? (
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              title={t("common.close")}
              className="-mr-2 -mt-1 inline-flex size-11 shrink-0 items-center justify-center rounded-control text-ink-2 hover:bg-ice-2 hover:text-ink"
            >
              <X aria-hidden="true" className="size-[18px]" strokeWidth={1.75} />
            </button>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}
